'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrRef } from '@/app/(home)/code/_prototype/session-panel/pr-ref';
import { GitPullRequest } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Command } from './types';
import { COMMAND_GROUPS } from './types';
import { cubicPrPath, formatPrRef, parsePrLink } from '@/app/(home)/code/_prototype/session-panel/pr-ref';
import { usePrNavigation } from '@/components/providers/pr-navigation';

/**
 * Reads the clipboard once the window has focus. Chromium reads it only for a focused window, and
 * the window the desktop hotkey brings forward takes focus a moment after the press.
 */
async function readClipboardOnceFocused(signal: AbortSignal): Promise<string> {
  if (!document.hasFocus()) {
    await new Promise(resolve => {
      window.addEventListener('focus', resolve, {
        once: true,
        signal
      });
    });
  }
  const text = await navigator.clipboard.readText();
  signal.throwIfAborted();
  return text;
}

/**
 * Lists the command that opens the pull request whose GitHub or cubic URL is on the clipboard. Each
 * press of the desktop hotkey reads the clipboard again, and the command lasts until the palette
 * closes.
 */
export function useClipboardPullRequestCommands(isOpen, hotkeyPresses) {
  const $ = _c(7);
  const {
    navigate
  } = usePrNavigation();
  const [pr, setPr] = useState(null);
  const lastReadPress = useRef(0);
  let t0;
  let t1;
  if ($[0] !== hotkeyPresses || $[1] !== isOpen) {
    t0 = () => {
      if (!isOpen || hotkeyPresses === lastReadPress.current) {
        return;
      }
      lastReadPress.current = hotkeyPresses;
      const controller = new AbortController();
      readClipboardOnceFocused(controller.signal).then(text => setPr(parsePrLink(text)), _temp);
      return () => {
        controller.abort();
        setPr(null);
      };
    };
    t1 = [isOpen, hotkeyPresses];
    $[0] = hotkeyPresses;
    $[1] = isOpen;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== navigate || $[5] !== pr) {
    t2 = pr ? [{
      id: "clipboard-pull-request",
      label: `Open ${formatPrRef(pr)}`,
      icon: <GitPullRequest />,
      action: () => navigate(cubicPrPath(pr), {
        source: "command_palette"
      }),
      safeExecution: "navigation" as const,
      group: COMMAND_GROUPS.CLIPBOARD.name
    }] : [];
    $[4] = navigate;
    $[5] = pr;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function _temp() {}